'use client'
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { motion, motionValue, useSpring, useTransform } from "motion/react";
import { useEffect, useId } from "react";
import react_use_measure from "react-use-measure";
import { cn } from "../../../utils/cn.js";
const TRANSITION = {
    type: 'spring',
    stiffness: 280,
    damping: 18,
    mass: 0.3
};
function Digit({ value, place }) {
    const valueRoundedToPlace = Math.floor(value / place) % 10;
    const initial = motionValue(valueRoundedToPlace);
    const animatedValue = useSpring(initial, TRANSITION);
    useEffect(()=>{
        animatedValue.set(valueRoundedToPlace);
    }, [
        animatedValue,
        valueRoundedToPlace
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: "relative inline-block w-[1ch] overflow-y-clip overflow-x-visible tabular-nums leading-none",
        children: [
            /*#__PURE__*/ jsx("div", {
                className: "invisible",
                children: "0"
            }),
            Array.from({
                length: 10
            }, (_, i)=>/*#__PURE__*/ jsx(SlidingNumber_Number, {
                    mv: animatedValue,
                    number: i
                }, i))
        ]
    });
}
function SlidingNumber_Number({ mv, number }) {
    const uniqueId = useId();
    const [ref, bounds] = react_use_measure();
    const y = useTransform(mv, (latest)=>{
        if (!bounds.height) return 0;
        const placeValue = latest % 10;
        const offset = (10 + number - placeValue) % 10;
        let memo = offset * bounds.height;
        if (offset > 5) memo -= 10 * bounds.height;
        return memo;
    });
    if (!bounds.height) return /*#__PURE__*/ jsx("span", {
        ref: ref,
        className: "invisible absolute",
        children: number
    });
    return /*#__PURE__*/ jsx(motion.span, {
        style: {
            y
        },
        layoutId: `${uniqueId}-${number}`,
        className: "absolute inset-0 flex items-center justify-center",
        transition: TRANSITION,
        ref: ref,
        children: number
    });
}
function SlidingNumber({ value, padStart = false, decimalSeparator = '.', className }) {
    const absValue = Math.abs(value);
    const [integerPart, decimalPart] = absValue.toString().split('.');
    const integerValue = parseInt(integerPart, 10);
    const paddedInteger = padStart && integerValue < 10 ? `0${integerPart}` : integerPart;
    const integerDigits = paddedInteger.split('');
    const integerPlaces = integerDigits.map((_, i)=>Math.pow(10, integerDigits.length - i - 1));
    return /*#__PURE__*/ jsxs("div", {
        className: cn('flex items-center', className),
        children: [
            value < 0 && '-',
            integerDigits.map((_, index)=>/*#__PURE__*/ jsx(Digit, {
                    value: integerValue,
                    place: integerPlaces[index]
                }, `pos-${integerPlaces[index]}`)),
            decimalPart && /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("span", {
                        children: decimalSeparator
                    }),
                    decimalPart.split('').map((_, index)=>/*#__PURE__*/ jsx(Digit, {
                            value: parseInt(decimalPart, 10),
                            place: Math.pow(10, decimalPart.length - index - 1)
                        }, `decimal-${index}`))
                ]
            })
        ]
    });
}
export { SlidingNumber };
